import React, { useState, useEffect } from 'react';
import { supabase, getCurrentUser, getUserProfile } from './supabaseClient';
import LandingPage from './LandingPage';
import DifficultySelection from './DifficultySelection';
import SudokuPuzzle from './SudokuPuzzle';
import ResultScreen from './ResultScreen';
import LeaderboardPage from './LeaderboardPage';
import LoginRegisterPage from './LoginRegisterPage';
import PasswordResetPage from './PasswordResetPage';
import FounderPage from './FounderPage';
import AboutPage from './AboutPage';
import AccountPage from './AccountPage';
import FounderHubPage from './FounderHubPage';
import AdminPuzzlePage from './AdminPuzzlePage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import TermsOfServicePage from './TermsOfServicePage';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [navigationHistory, setNavigationHistory] = useState(['landing']);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  
  // Context tracking for smart back navigation
  const [cameFromPuzzle, setCameFromPuzzle] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState('hard');
  const [selectedMode, setSelectedMode] = useState('standard');
  const [solveData, setSolveData] = useState(null);
  
  // ANTI-CHEAT: Attempt state tracking
  // Structure: { used: boolean, mode: 'standard'|'relaxed'|null, completed: boolean }
  const [attemptState, setAttemptState] = useState({
    easy: { used: false, mode: null, completed: false },
    hard: { used: false, mode: null, completed: false },
    master: { used: false, mode: null, completed: false }
  });

  // ANTI-CHEAT: Mark attempt as used when puzzle is entered
  const markAttemptUsed = (difficulty, mode) => {
    setAttemptState(prev => ({
      ...prev,
      [difficulty]: {
        ...prev[difficulty],
        used: true,
        mode: mode
      }
    }));
  };

  // ANTI-CHEAT: Mark puzzle as completed
  const markPuzzleCompleted = (difficulty) => {
    setAttemptState(prev => ({
      ...prev,
      [difficulty]: {
        ...prev[difficulty],
        completed: true
      }
    }));
  };

  // SESSION MANAGEMENT: Check for existing session on mount
  useEffect(() => {
    checkSession();
    
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        await loadUserData(session.user);
      } else if (event === 'SIGNED_OUT') {
        setIsLoggedIn(false);
        setCurrentUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Handle URL parameters from email links (after auth is loaded)
  useEffect(() => {
    if (isLoadingAuth) return; // Wait for auth to load
    
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    
    if (!action) return;
    
    if (action === 'manage-preferences') {
      if (isLoggedIn) {
        navigateTo('account');
      } else {
        navigateTo('login');
      }
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    } else if (action === 'unsubscribe') {
      if (isLoggedIn) {
        navigateTo('account', { showUnsubscribe: true });
      } else {
        navigateTo('login');
      }
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isLoadingAuth, isLoggedIn]);

  const checkSession = async () => {
    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session check timed out')), 5000)
      );
      
      // Race the session check against the timeout
      const sessionPromise = supabase.auth.getSession();
      
      const { data: { session }, error } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]);

      if (error) {
        console.error('Session error:', error);
        setIsLoadingAuth(false);
        return;
      }

      if (session) {
        await loadUserData(session.user);
      } else {
        setIsLoadingAuth(false);
      }
    } catch (error) {
      console.error('Error checking session:', error);
      // On timeout or error, just continue without auth
      setIsLoadingAuth(false);
    }
  };

  const loadUserData = async (user, retryCount = 0) => {
    try {
      setCurrentUser(user);
      setIsLoggedIn(true);
      
      // Try to get cached profile from localStorage first
      const cachedProfile = localStorage.getItem('userProfile');
      if (cachedProfile && !userProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          setUserProfile(parsed);
          console.log('Loaded cached profile');
        } catch (e) {
          console.error('Error parsing cached profile:', e);
        }
      }
      
      // Create timeout for profile fetch (10 seconds)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timed out')), 10000)
      );
      
      // Race profile fetch against timeout
      const profilePromise = getUserProfile(user.id);
      
      const profile = await Promise.race([
        profilePromise,
        timeoutPromise
      ]);
      
      if (profile) {
        setUserProfile(profile);
        // Cache profile in localStorage for future use
        localStorage.setItem('userProfile', JSON.stringify(profile));
        console.log('Profile loaded successfully');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      
      // Retry once if this is the first attempt
      if (retryCount === 0) {
        console.log('Retrying profile fetch...');
        setTimeout(() => loadUserData(user, 1), 2000);
        return;
      }
      
      // On final failure, DON'T reset userProfile to null
      // Keep existing profile or cached profile
      console.warn('Profile fetch failed, using cached data');
      
      // Only try to load from cache if we don't already have a profile
      if (!userProfile) {
        const cachedProfile = localStorage.getItem('userProfile');
        if (cachedProfile) {
          try {
            const parsed = JSON.parse(cachedProfile);
            setUserProfile(parsed);
            console.log('Using cached profile after fetch failure');
          } catch (e) {
            console.error('Error parsing cached profile:', e);
          }
        }
      }
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const handleLogin = async (user) => {
    await loadUserData(user);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setUserProfile(null);
    // Clear cached profile
    localStorage.removeItem('userProfile');
    navigateTo('landing');
  };

  const navigateTo = (page, context = {}) => {
    // Update context flags
    if (context.fromPuzzle) {
      setCameFromPuzzle(true);
    }
    if (context.difficulty) {
      setSelectedDifficulty(context.difficulty);
    }
    if (context.mode) {
      setSelectedMode(context.mode);
    }
    if (context.solveData) {
      setSolveData(context.solveData);
    }
    if (context.loggedIn !== undefined) {
      setIsLoggedIn(context.loggedIn);
    }

    // Update navigation history
    setNavigationHistory(prev => [...prev, page]);
    setCurrentPage(page);
  };

  const goBack = (fromPage) => {
    // Explicit back button behavior based on navigation spec
    switch(fromPage) {
      case 'difficulty':
        navigateTo('landing');
        break;
      
      case 'puzzle':
        // Show confirmation modal (handled in SudokuPuzzle component)
        navigateTo('difficulty');
        break;
      
      case 'result':
        // Never return to active puzzle
        navigateTo('difficulty');
        break;
      
      case 'leaderboard':
        // If came from solve, go to result; otherwise difficulty
        if (cameFromPuzzle) {
          navigateTo('result');
        } else {
          navigateTo('difficulty');
        }
        break;
      
      case 'login':
        // Context-aware back
        if (cameFromPuzzle) {
          navigateTo('result');
        } else {
          navigateTo('landing');
        }
        break;
      
      case 'passwordreset':
        // Always return to login
        navigateTo('login');
        break;
      
      case 'admin':
      case 'founder':
      case 'about':
      case 'account':
      case 'founderhub':
      case 'privacy':
      case 'terms':
        // Return to previous page (use history or default to landing)
        if (navigationHistory.length > 1) {
          const previousPage = navigationHistory[navigationHistory.length - 2];
          setCurrentPage(previousPage);
          setNavigationHistory(prev => prev.slice(0, -1));
        } else {
          navigateTo('landing');
        }
        break;
      
      default:
        navigateTo('landing');
    }
  };

  return (
    <div className="App">
      {isLoadingAuth ? (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          color: 'white',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ fontSize: '18px', fontWeight: '600' }}>Loading...</div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : (
        <>
          {currentPage === 'landing' && (
            <LandingPage 
              navigateTo={navigateTo} 
              isLoggedIn={isLoggedIn}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'difficulty' && (
            <DifficultySelection 
              navigateTo={navigateTo}
              goBack={() => goBack('difficulty')}
              isLoggedIn={isLoggedIn}
              attemptState={attemptState}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'puzzle' && (
            <SudokuPuzzle 
              navigateTo={navigateTo}
              goBack={() => goBack('puzzle')}
              difficulty={selectedDifficulty}
              mode={selectedMode}
              attemptState={attemptState}
              markAttemptUsed={markAttemptUsed}
              markPuzzleCompleted={markPuzzleCompleted}
            />
          )}
          
          {currentPage === 'result' && (
            <ResultScreen 
              navigateTo={navigateTo}
              goBack={() => goBack('result')}
              isLoggedIn={isLoggedIn}
              currentUser={currentUser}
              userProfile={userProfile}
              solveData={solveData}
            />
          )}
          
          {currentPage === 'leaderboard' && (
            <LeaderboardPage 
              navigateTo={navigateTo}
              goBack={() => goBack('leaderboard')}
              isLoggedIn={isLoggedIn}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'login' && (
            <LoginRegisterPage 
              navigateTo={navigateTo}
              goBack={() => goBack('login')}
              cameFromPuzzle={cameFromPuzzle}
              onLogin={handleLogin}
              solveData={solveData}
            />
          )}
          
          {currentPage === 'passwordreset' && (
            <PasswordResetPage 
              navigateTo={navigateTo}
              goBack={() => goBack('passwordreset')}
            />
          )}
          
          {currentPage === 'founder' && (
            <FounderPage 
              navigateTo={navigateTo}
              goBack={() => goBack('founder')}
              isLoggedIn={isLoggedIn}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'about' && (
            <AboutPage 
              navigateTo={navigateTo}
              goBack={() => goBack('about')}
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
            />
          )}
          
          {currentPage === 'privacy' && (
            <PrivacyPolicyPage 
              navigateTo={navigateTo}
              goBack={() => goBack('privacy')}
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
            />
          )}
          
          {currentPage === 'terms' && (
            <TermsOfServicePage 
              navigateTo={navigateTo}
              goBack={() => goBack('terms')}
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
            />
          )}
          
          {currentPage === 'account' && (
            <AccountPage 
              navigateTo={navigateTo}
              goBack={() => goBack('account')}
              isLoggedIn={isLoggedIn}
              currentUser={currentUser}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'founderhub' && (
            <FounderHubPage 
              navigateTo={navigateTo}
              goBack={() => goBack('founderhub')}
              isLoggedIn={isLoggedIn}
              currentUser={currentUser}
              userProfile={userProfile}
            />
          )}
          
          {currentPage === 'admin' && (
            <AdminPuzzlePage 
              navigateTo={navigateTo}
              goBack={() => goBack('admin')}
              isLoggedIn={isLoggedIn}
              userProfile={userProfile}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
